// Cattle weigh-ins module-level cache. Verbatim extract from main.jsx.
import {wcfSelectAll} from './pagination.js';
import type {SupabaseErrorLike, SupabaseClient} from './supabaseTypes.js';

// `select('*')` returns whole weigh_ins rows and callers consume the rest of
// the columns, so only the one column THIS module reads is modelled. The rest
// stay `unknown` rather than being invented into a schema TypeScript cannot
// verify. `entered_at` is optional because the sorts below already defend
// against it being absent — the type follows the code, not the other way round.
export type WeighInRow = {entered_at?: string | null; [column: string]: unknown};

// A weigh_in_sessions row as narrowed by `.select('id')`. weigh_in_sessions.id
// is `text NOT NULL` (archive/001_cattle_module.sql).
type SessionIdRow = {id: string};

export type CacheOptions = {throwOnError?: boolean};

// ============================================================================
// loadCattleWeighIns — the uncached, signal-aware primitive (B3.3)
// ============================================================================
// The cached wrapper below cannot be used by a route resource, for three
// reasons that are all disqualifying on their own:
//
//   1. its data and timestamp are MODULE-GLOBAL, so they outlive a sign-out and
//      are not scoped to an authenticated subject — user B could be served
//      user A's rows on a shared browser;
//   2. it takes no AbortSignal, so a navigation away cannot cancel it and a
//      late completion cannot be suppressed;
//   3. its 30-second TTL is a second, invisible cache sitting underneath
//      whatever caching the resource layer does — two caches with different
//      invalidation rules for one table.
//
// So the read itself is available here uncached and cancellable. It always
// throws on failure: a resource boundary translates that into bounded route UI,
// and a fake-empty result would be indistinguishable from a genuinely empty
// farm.
//
// The cached wrapper is deliberately NOT refactored to call this. Its soft mode
// returns PARTIAL rows on a page error (`_cattleWeighInsCache || rows`), which a
// throwing primitive cannot reproduce; rewriting it would be a behaviour change
// to nine existing callers for no benefit to the pilot. The duplication is
// temporary and ends when the wrapper is retired.
//
// abortSignal is applied only when the builder exposes it, matching the
// convention already used in the maintenance layer, so plain fakes work in
// tests without simulating the whole PostgREST builder. The generic below says
// exactly that: whatever builder comes in comes back out, and `abortSignal` is
// OPTIONAL on it because the fakes are the reason the runtime check exists.
// ============================================================================
type MaybeAbortable<Builder> = {abortSignal?: (signal: AbortSignal) => Builder};

export async function loadCattleWeighIns(
  sb: SupabaseClient,
  {signal}: {signal?: AbortSignal} = {},
): Promise<WeighInRow[]> {
  const withSignal = <Builder extends MaybeAbortable<Builder>>(b: Builder): Builder =>
    signal && b && typeof b.abortSignal === 'function' ? b.abortSignal(signal) : b;

  const sR = await withSignal(sb.from('weigh_in_sessions').select('id').eq('species', 'cattle'));
  if (sR.error) throw new Error('cattle weigh-in sessions: ' + (sR.error.message || sR.error));

  const ids = (sR.data || []).map((s: SessionIdRow) => s.id);
  // A farm with no cattle weigh-in sessions is a legitimate empty, not a
  // failure, and must stay distinguishable from one.
  if (ids.length === 0) return [];

  // wcfSelectAll intentionally swallows page errors and returns what it has, so
  // the error is observed through the build closure instead. A partial page is
  // never returned as truth here.
  //
  // The capture is a one-slot array rather than a `let`, because TypeScript's
  // control-flow analysis does not observe assignments made inside a callback:
  // a `let pageError: SupabaseErrorLike | null = null` still reads as `null` at
  // the check below, so `.message` would not exist on it. `.at(-1)` reproduces
  // the previous last-assignment-wins semantics exactly, and wcfSelectAll stops
  // at the first failing page so at most one error is ever pushed.
  const pageErrorSlot: SupabaseErrorLike[] = [];
  const rows = await wcfSelectAll<WeighInRow>(async (f, t) => {
    // Offset pages need one unique database order. Sorting after collection
    // cannot recover rows skipped when unordered pages use different plans.
    const res = await withSignal(sb.from('weigh_ins').select('*').in('session_id', ids).order('id').range(f, t));
    if (res.error) pageErrorSlot.push(res.error);
    return res;
  });
  const pageError = pageErrorSlot.at(-1);
  if (pageError) throw new Error('cattle weigh_ins: ' + (pageError.message || pageError));

  // Descending by entered_at so .find() returns the latest match — the ordering
  // every existing consumer already relies on.
  rows.sort((a, b) => (b.entered_at || '').localeCompare(a.entered_at || ''));
  return rows;
}

// ── Cattle weigh_ins cache ──────────────────────────────────────────────────
// Each cattle sub-view (Home / Herds / Batches / Weigh-Ins) needs the full
// species-scoped weigh_ins list. Fetching ~2k rows across 3 paginated round-
// trips PER view every time the user navigates is wasteful. Module-level
// cache with a short TTL lets sub-tab navigation reuse the data.
// Invalidate after any write (tag reconcile, session delete, etc.) to stay
// correct. Sorted descending by entered_at so .find() returns the latest match.
let _cattleWeighInsCache: WeighInRow[] | null = null;
let _cattleWeighInsCacheAt = 0;
const CATTLE_WEIGH_INS_TTL_MS = 30000; // 30s — fresh enough for nav, short enough to catch recent writes
// opts.throwOnError (default false): when true, a HARD read failure on either
// query — the sessions list or any weigh_ins page — throws instead of silently
// resolving to []. The cold-boot Cattle Forecast loader passes this so a
// raced/errored read routes through its bounded retry + recoverable-notice path
// rather than poisoning the page with a fake-empty payload (0 finish
// candidates until reload). A failed read is NEVER cached, so the next call
// (post-invalidate) re-reads fresh. Default callers keep the legacy soft
// contract: return the last-good cache or [], never throw. A genuinely empty
// farm (no cattle weigh-in sessions) still settles to [] with no throw under
// either mode — that is a legitimate empty, not a failure.
export async function loadCattleWeighInsCached(sb: SupabaseClient, opts: CacheOptions = {}): Promise<WeighInRow[]> {
  const throwOnError = !!opts.throwOnError;
  if (_cattleWeighInsCache && Date.now() - _cattleWeighInsCacheAt < CATTLE_WEIGH_INS_TTL_MS) {
    return _cattleWeighInsCache;
  }
  const sR = await sb.from('weigh_in_sessions').select('id').eq('species', 'cattle');
  if (sR.error) {
    // Do NOT cache a failed sessions read — let the next call retry fresh.
    if (throwOnError) throw new Error('loadCattleWeighInsCached sessions: ' + (sR.error.message || sR.error));
    return _cattleWeighInsCache || [];
  }
  const ids = (sR.data || []).map((s: SessionIdRow) => s.id);
  if (ids.length === 0) {
    _cattleWeighInsCache = [];
    _cattleWeighInsCacheAt = Date.now();
    return _cattleWeighInsCache;
  }
  // Observe a page-read error through the build closure WITHOUT changing the
  // wcfSelectAll contract (it intentionally swallows errors and returns the
  // rows gathered so far). A partial/errored page must not be cached as truth.
  // One-slot array rather than a `let` for the closure-capture reason explained
  // on the uncached primitive above.
  const pageErrorSlot: SupabaseErrorLike[] = [];
  const rows = await wcfSelectAll<WeighInRow>(async (f, t) => {
    // Match the uncached reader's stable pagination before sorting by date.
    const res = await sb.from('weigh_ins').select('*').in('session_id', ids).order('id').range(f, t);
    if (res.error) pageErrorSlot.push(res.error);
    return res;
  });
  const pageError = pageErrorSlot.at(-1);
  if (pageError) {
    if (throwOnError) throw new Error('loadCattleWeighInsCached weigh_ins: ' + (pageError.message || pageError));
    return _cattleWeighInsCache || rows;
  }
  rows.sort((a, b) => (b.entered_at || '').localeCompare(a.entered_at || ''));
  _cattleWeighInsCache = rows;
  _cattleWeighInsCacheAt = Date.now();
  return _cattleWeighInsCache;
}
export function invalidateCattleWeighInsCache(): void {
  _cattleWeighInsCache = null;
  _cattleWeighInsCacheAt = 0;
}
